import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Handshake, Percent, Rocket, UserCheck } from "lucide-react";
import partnerBadge from "@/assets/chathero-partner-badge.png";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { useLanguage } from "@/hooks/useLanguage";

const PartnerHero = () => {
  const { t, localizePath } = useLanguage();
  const p = "partnerPage";

  return (
    <section className="relative bg-secondary/30 pt-40 pb-20 overflow-hidden">
      <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-accent/20 rounded-full blur-3xl -z-10" />
      <div className="absolute bottom-0 left-1/4 w-[300px] h-[300px] bg-primary/10 rounded-full blur-3xl -z-10" />

      <div className="container mx-auto px-4 md:px-6">
        <Breadcrumb className="mb-8">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to={localizePath("/")} className="text-muted-foreground hover:text-accent">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="text-accent">{t(`${p}.breadcrumb`)}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Handshake className="w-4 h-4" />
              {t(`${p}.badge`)}
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary mb-6 leading-tight">
              {t(`${p}.heroTitle1`)}{" "}
              <span className="text-accent">{t(`${p}.heroTitle2`)}</span>
            </h1>

            <p className="text-xl text-muted-foreground mb-4">
              {t(`${p}.heroSubtitle`)}
            </p>

            <p className="text-lg text-foreground/80 mb-8" dangerouslySetInnerHTML={{ __html: t(`${p}.heroDescription`) }} />

            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 text-base px-8" asChild>
                <Link to={localizePath("/kontakt")}>
                  {t(`${p}.ctaPrimary`)}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="border-primary/30 text-primary hover:bg-primary/5 text-base" asChild>
                <Link to={localizePath("/preise")}>{t(`${p}.ctaSecondary`)}</Link>
              </Button>
            </div>
          </div>

          <div className="relative flex items-center justify-center py-20">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-accent/20 to-primary/10 rounded-full blur-3xl" />
              <img src={partnerBadge} alt="Chathero Partner Badge" className="relative z-10 w-64 md:w-80 lg:w-96 object-contain drop-shadow-2xl" />

              <div className="absolute -top-16 left-1/2 -translate-x-1/2 bg-background/90 backdrop-blur-md border border-border/60 rounded-2xl px-4 py-2.5 shadow-xl z-20 animate-fade-in whitespace-nowrap" style={{ animationDelay: "0.2s" }}>
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-accent/15 flex items-center justify-center shrink-0"><Percent className="w-3.5 h-3.5 text-accent" /></div>
                  <div className="text-sm font-bold text-primary">{t(`${p}.overlay1`)}</div>
                </div>
              </div>

              <div className="absolute -bottom-16 -left-16 bg-background/90 backdrop-blur-md border border-border/60 rounded-2xl px-4 py-2.5 shadow-xl z-20 animate-fade-in whitespace-nowrap" style={{ animationDelay: "0.5s" }}>
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-accent/15 flex items-center justify-center shrink-0"><Rocket className="w-3.5 h-3.5 text-accent" /></div>
                  <div className="text-sm font-bold text-primary">{t(`${p}.overlay2`)}</div>
                </div>
              </div>

              <div className="absolute -bottom-16 -right-16 bg-background/90 backdrop-blur-md border border-border/60 rounded-2xl px-4 py-2.5 shadow-xl z-20 animate-fade-in whitespace-nowrap" style={{ animationDelay: "0.8s" }}>
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-accent/15 flex items-center justify-center shrink-0"><UserCheck className="w-3.5 h-3.5 text-accent" /></div>
                  <div className="text-sm font-bold text-primary">{t(`${p}.overlay3`)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PartnerHero;
